import { ClassificationWithoutTitle, EventMeta } from '../../../interfaces/meta.interface';

type NewTransportRuleDescFields = {
    name: string;
};

export function getTransportRule(action: 'created' | 'enabled'): ClassificationWithoutTitle<NewTransportRuleDescFields>[] {
    return [
        {
            severity: 'critical',
            description: (fields) => `A transport rule "${fields.name}" was ${action} from a suspicious IP`,
            longDescription: (fields) =>
                `A transport rule "${fields.name}" was ${action}. The action originated from a suspicious IP that is not on your allowed list. Enabling transport rules can significantly alter email routing, filtering, or blocking. Because this originated from a suspicious IP, review immediately to determine if it was legitimate or malicious.`,
            predicates: {
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
                isFraudulentIP: true,
            },
        },
        {
            severity: 'danger',
            description: (fields) => `A transport rule "${fields.name}" was ${action} from an unapproved IP`,
            longDescription: (fields) =>
                `A transport rule "${fields.name}" was ${action}. The action originated from an unapproved IP that is not in your allowed IPs. Enabling transport rules can alter how emails are routed, filtered, or blocked. If this was not an expected change, review promptly to confirm whether the activity was legitimate or a potential security risk.`,
            predicates: {
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
            },
        },
        {
            severity: 'warning',
            description: (fields) => `A transport rule "${fields.name}" was ${action} from a suspicious IP`,
            longDescription: (fields) =>
                `The transport rule "${fields.name}" was ${action}. While enabling transport rules is part of normal email administration, the action came from a suspicious IP and may suggest unauthorized manipulation. Review the rule and its origin to confirm whether the change was expected or potentially malicious.`,
            predicates: {
                isFraudulentIP: true,
            },
        },
        {
            severity: 'info',
            description: (fields) => `A transport rule "${fields.name}" was ${action} from an unapproved IP`,
            longDescription: (fields) =>
                `The transport rule "${fields.name}" was ${action}. Enabling transport rules is part of routine administration, but it can alter how emails are processed, redirected, or blocked. Review the rule and its settings to ensure it aligns with organizational policies and was performed intentionally.`,
        },
    ];
}

const newTransportRule: EventMeta<NewTransportRuleDescFields> = {
    title: 'Transport Rule Created',
    default: {
        description: 'Transport Rule Created',
        longDescription: 'A transport rule has been created.',
        severity: 'info',
    },
    descriptionExtractor: (log: any) => ({
        name: log.name,
    }),
    classifications: getTransportRule('created'),
};

export default newTransportRule;
